import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { VscAdd, VscEdit, VscTrash, VscCheck, VscClose, VscChevronDown, VscChevronRight, VscFile, VscSearch, VscCopy } from 'react-icons/vsc';
import { getJobPackages, createJobPackage, updateJobPackage, deleteJobPackage, markJobPkgRead, patchJobPkgStatus } from '../../api/jobPackages';
import { getUsers } from '../../api/users';
import { getCables, patchCablePulled } from '../../api/cables';
import { getItems } from '../../api/items';
import { getFolders, getFiles } from '../../api/projectFiles';
import Avatar from '../common/Avatar';
import RowNotices from '../common/RowNotices';
import { PhotoButton } from '../common/PhotoGallery';
import { buildCableGroups, buildItemGroups, matchesGroupFilter, GroupFilterSelects, NO_GROUP_FILTER } from '../../utils/listGroups';
import './JobPackageView.css';

function padNum(n) { return String(n ?? '').padStart(3, '0'); }

function cableEndpoint(cable, end) {
  const brk  = end === 'from' ? cable.fromBreaker : cable.toBreaker;
  const item = end === 'from' ? cable.from         : cable.to;
  if (brk) return `${brk.tag}${brk.type ? ` — ${brk.type}` : ''}${brk.switchboard?.name ? ` (${brk.switchboard.name})` : ''}`;
  return item?.description || '—';
}

const STATUS_LABELS = { planned: 'Planned', 'in-progress': 'In Progress', completed: 'Completed' };
const STATUS_COLORS = { planned: '#4fc1ff', 'in-progress': '#f0a04b', completed: '#4ec9b0' };
const STATUS_CYCLE  = { planned: 'in-progress', 'in-progress': 'completed', completed: 'planned' };

const ROW_H    = 32;
const HDR_H    = 40;
const BAR_PAD  = 4;
const PX_PER_DAY = 5;

function daysBetween(a, b) {
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}
function addDays(date, n) {
  const d = new Date(date); d.setDate(d.getDate() + n); return d;
}
function isoDate(d) { return new Date(d).toISOString().slice(0, 10); }
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
}

function buildTimeRange(pkgs) {
  const dated = pkgs.filter(p => p.startDate && p.endDate);
  if (!dated.length) {
    const today = new Date(); today.setDate(1);
    return { start: today, end: addDays(today, 90) };
  }
  let minD = new Date(dated[0].startDate);
  let maxD = new Date(dated[0].endDate);
  dated.forEach(p => {
    const s = new Date(p.startDate), e = new Date(p.endDate);
    if (s < minD) minD = s;
    if (e > maxD) maxD = e;
  });
  return { start: addDays(minD, -7), end: addDays(maxD, 14) };
}

function buildMonthLabels(start, end) {
  const labels = [];
  const cur = new Date(start); cur.setDate(1);
  while (cur <= end) {
    const monthStart = new Date(cur);
    const nextMonth  = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    const from = Math.max(0, daysBetween(start, monthStart));
    const to   = daysBetween(start, nextMonth);
    if (to > from) {
      labels.push({ label: monthStart.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }), x: from * PX_PER_DAY, w: (to - from) * PX_PER_DAY });
    }
    cur.setMonth(cur.getMonth() + 1);
  }
  return labels;
}

function GanttOverview({ packages }) {
  const dated = packages.filter(p => p.startDate && p.endDate);
  if (!dated.length) return null;
  const { start, end } = buildTimeRange(dated);
  const totalDays = daysBetween(start, end);
  const tlWidth   = totalDays * PX_PER_DAY;
  const monthLabels = buildMonthLabels(start, end);

  return (
    <div className="jp-gantt-wrap">
      <div className="jp-gantt-scroll" style={{ minWidth: tlWidth }}>
        <div className="jp-gantt-hdr" style={{ width: tlWidth, height: HDR_H }}>
          {monthLabels.map((m, i) => (
            <div key={i} className="jp-gantt-month" style={{ left: m.x, width: m.w }}>{m.label}</div>
          ))}
          {/* Today line */}
          {(() => {
            const tx = daysBetween(start, new Date()) * PX_PER_DAY;
            return tx >= 0 && tx <= tlWidth
              ? <div className="jp-gantt-today" style={{ left: tx }} />
              : null;
          })()}
        </div>
        {dated.map(p => {
          const bx = Math.max(0, daysBetween(start, p.startDate) * PX_PER_DAY);
          const bw = Math.max(4, daysBetween(p.startDate, p.endDate) * PX_PER_DAY);
          return (
            <div key={p._id} className="jp-gantt-row" style={{ height: ROW_H }}>
              <div className="jp-gantt-bar" style={{ left: bx + BAR_PAD, width: Math.max(bw - BAR_PAD * 2, 4), background: p.color || STATUS_COLORS[p.status] || '#0e639c' }}
                title={`${p.name}: ${fmtDate(p.startDate)} → ${fmtDate(p.endDate)}`}>
                <span className="jp-gantt-bar-label">{p.name}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function emptyForm(projectId) {
  return {
    project: projectId,
    name: '', orderNo: '', description: '', status: 'planned',
    startDate: isoDate(new Date()), endDate: isoDate(addDays(new Date(), 14)),
    color: '#0e639c', assignees: [], cables: [], items: [], files: [],
  };
}

function FilePicker({ projectId, selected, onToggle, onClose }) {
  const [folders, setFolders]   = useState([]);
  const [files,   setFiles]     = useState([]);
  const [folderId, setFolderId] = useState(null);

  useEffect(() => {
    getFolders(projectId).then(setFolders).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (folderId === null) { setFiles([]); return; }
    getFiles(projectId, folderId).then(setFiles).catch(() => {});
  }, [projectId, folderId]);

  return (
    <div className="jp-filepicker">
      <div className="jp-filepicker-header">
        <span>Browse Files</span>
        <button className="btn-icon" onClick={onClose}><VscClose size={13} /></button>
      </div>
      <div className="jp-filepicker-body">
        <div className="jp-filepicker-folders">
          {folders.map(f => (
            <button key={f._id}
              className={`jp-filepicker-folder${folderId === f._id ? ' active' : ''}`}
              onClick={() => setFolderId(f._id)}>
              <VscFile size={12} /> {f.name}
            </button>
          ))}
          {folders.length === 0 && <span className="jp-filepicker-empty">No folders.</span>}
        </div>
        <div className="jp-filepicker-files">
          {folderId === null && <span className="jp-filepicker-empty">Select a folder.</span>}
          {files.map(f => {
            const sel = selected.some(s => s.fileId === f._id);
            return (
              <label key={f._id} className="jp-filepicker-file">
                <input type="checkbox" checked={sel}
                  onChange={() => onToggle({ fileId: f._id, name: f.filename })} />
                <VscFile size={12} /> {f.filename}
              </label>
            );
          })}
          {folderId !== null && files.length === 0 && <span className="jp-filepicker-empty">No files.</span>}
        </div>
      </div>
    </div>
  );
}

function PackageForm({ data, setData, users, allCables, allItems, onSave, onCancel, isNew }) {
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [cableSearch, setCableSearch] = useState('');
  const [itemSearch, setItemSearch]   = useState('');
  const [cableGroupFilter, setCableGroupFilter] = useState(NO_GROUP_FILTER);
  const [itemGroupFilter, setItemGroupFilter]   = useState(NO_GROUP_FILTER);
  const cableGroups = useMemo(() => buildCableGroups(allCables, allItems), [allCables, allItems]);
  const itemGroups  = useMemo(() => buildItemGroups(allItems), [allItems]);

  const set = (k) => (v) => setData(d => ({ ...d, [k]: v }));

  const toggleAssignee = (uid) => setData(d => ({
    ...d, assignees: d.assignees.includes(uid) ? d.assignees.filter(a => a !== uid) : [...d.assignees, uid],
  }));
  const toggleCable = (cid) => setData(d => ({
    ...d, cables: d.cables.includes(cid) ? d.cables.filter(c => c !== cid) : [...d.cables, cid],
  }));
  const toggleItem = (iid) => setData(d => ({
    ...d, items: d.items.includes(iid) ? d.items.filter(i => i !== iid) : [...d.items, iid],
  }));
  const toggleFile = (f) => setData(d => {
    const has = d.files.some(x => x.fileId === f.fileId);
    return { ...d, files: has ? d.files.filter(x => x.fileId !== f.fileId) : [...d.files, f] };
  });

  const filteredCables = allCables.filter(c => {
    if (!matchesGroupFilter(cableGroups.groups, c._id, cableGroupFilter)) return false;
    if (!cableSearch) return true;
    const q = cableSearch.toLowerCase();
    return (c.tag?.toLowerCase().includes(q)) ||
           cableEndpoint(c, 'from').toLowerCase().includes(q) ||
           cableEndpoint(c, 'to').toLowerCase().includes(q);
  });
  const filteredItems = allItems.filter(i =>
    matchesGroupFilter(itemGroups.groups, i._id, itemGroupFilter) && (
      !itemSearch || i.tag?.toLowerCase().includes(itemSearch.toLowerCase()) ||
      i.description?.toLowerCase().includes(itemSearch.toLowerCase()))
  );

  // Adds every row the filters show (keeps what was already selected)
  const selectAllShown = (key, rows) => setData(d => ({ ...d, [key]: [...new Set([...d[key], ...rows.map(r => r._id)])] }));

  return (
    <div className="jp-form">
      <div className="jp-form-row">
        <label className="jp-form-label">Name *
          <input className="jp-input" value={data.name} onChange={e => set('name')(e.target.value)} autoFocus />
        </label>
        <label className="jp-form-label" style={{ maxWidth: 140 }}>Order No.
          <input className="jp-input" value={data.orderNo || ''} onChange={e => set('orderNo')(e.target.value)} placeholder="e.g. PO-1234" />
        </label>
        <label className="jp-form-label" style={{ maxWidth: 140 }}>Status
          <select className="jp-input" value={data.status} onChange={e => set('status')(e.target.value)}>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <div className="jp-form-row">
        <label className="jp-form-label">Start Date
          <input type="date" className="jp-input" value={isoDate(data.startDate)} onChange={e => set('startDate')(e.target.value)} />
        </label>
        <label className="jp-form-label">End Date
          <input type="date" className="jp-input" value={isoDate(data.endDate)} onChange={e => set('endDate')(e.target.value)} />
        </label>
        <label className="jp-form-label">Colour
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="color" value={data.color || '#0e639c'} onChange={e => set('color')(e.target.value)}
              style={{ width: 36, height: 28, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }} />
          </div>
        </label>
      </div>
      <label className="jp-form-label">Description
        <textarea className="jp-input jp-textarea" value={data.description || ''} onChange={e => set('description')(e.target.value)} rows={3} />
      </label>

      {/* Assignees */}
      <div className="jp-form-section">Assignees</div>
      <div className="jp-checklist">
        {users.map(u => (
          <label key={u._id} className="jp-checkitem">
            <input type="checkbox" checked={data.assignees.includes(u._id)}
              onChange={() => toggleAssignee(u._id)} />
            <Avatar user={u} size={18} />
            <span>{u.displayName || u.username}</span>
          </label>
        ))}
      </div>

      {/* Cables */}
      <div className="jp-form-section">Cables <span className="jp-muted">({data.cables.length} selected)</span></div>
      <div className="jp-search-row">
        <VscSearch size={12} />
        <input className="jp-input jp-search-input" placeholder="Filter cables…" value={cableSearch}
          onChange={e => setCableSearch(e.target.value)} />
        <GroupFilterSelects options={cableGroups.options} value={cableGroupFilter} onChange={setCableGroupFilter}
          className="jp-input group-filter" />
        <button className="btn-secondary jp-select-all" disabled={!filteredCables.length}
          onClick={() => selectAllShown('cables', filteredCables)}>Select all shown ({filteredCables.length})</button>
      </div>
      <div className="jp-checklist jp-checklist-scroll">
        {filteredCables.map(c => (
          <label key={c._id} className="jp-checkitem">
            <input type="checkbox" checked={data.cables.includes(c._id)}
              onChange={() => toggleCable(c._id)} />
            <span className="jp-tag" style={{ flexShrink: 0 }}>{c.tag}</span>
            <span className="jp-muted">{cableEndpoint(c, 'from')} → {cableEndpoint(c, 'to')}</span>
            {c.cableType?.name && <span className="jp-muted" style={{ flexShrink: 0 }}>· {c.cableType.name}</span>}
            {c.length_m != null && <span className="jp-muted" style={{ flexShrink: 0 }}>{c.length_m} m</span>}
          </label>
        ))}
        {filteredCables.length === 0 && <span className="jp-muted">No cables found.</span>}
      </div>

      {/* Items */}
      <div className="jp-form-section">Items <span className="jp-muted">({data.items.length} selected)</span></div>
      <div className="jp-search-row">
        <VscSearch size={12} />
        <input className="jp-input jp-search-input" placeholder="Filter items…" value={itemSearch}
          onChange={e => setItemSearch(e.target.value)} />
        <GroupFilterSelects options={itemGroups.options} value={itemGroupFilter} onChange={setItemGroupFilter}
          className="jp-input group-filter" />
        <button className="btn-secondary jp-select-all" disabled={!filteredItems.length}
          onClick={() => selectAllShown('items', filteredItems)}>Select all shown ({filteredItems.length})</button>
      </div>
      <div className="jp-checklist jp-checklist-scroll">
        {filteredItems.map(i => (
          <label key={i._id} className="jp-checkitem">
            <input type="checkbox" checked={data.items.includes(i._id)}
              onChange={() => toggleItem(i._id)} />
            <span className="jp-tag" style={{ flexShrink: 0 }}>{i.tag}</span>
            <span className="jp-muted">{i.description}</span>
            {i.room && <span className="jp-muted" style={{ flexShrink: 0 }}>· {String(i.room.number ?? '').padStart(3,'0')} {i.room.text}</span>}
          </label>
        ))}
        {filteredItems.length === 0 && <span className="jp-muted">No items found.</span>}
      </div>

      {/* Files */}
      <div className="jp-form-section">Documents & Drawings</div>
      <div className="jp-files-selected">
        {data.files.map(f => (
          <span key={f.fileId} className="jp-file-chip">
            <VscFile size={11} /> {f.name}
            <button className="jp-file-chip-remove" onClick={() => toggleFile(f)}><VscClose size={10} /></button>
          </span>
        ))}
        {data.files.length === 0 && <span className="jp-muted">No files linked.</span>}
      </div>
      <button className="btn-secondary jp-add-files-btn" onClick={() => setShowFilePicker(v => !v)}>
        Browse Files
      </button>
      {showFilePicker && (
        <FilePicker
          projectId={data.project}
          selected={data.files}
          onToggle={toggleFile}
          onClose={() => setShowFilePicker(false)}
        />
      )}

      <div className="jp-form-actions">
        <button className="btn-primary" onClick={onSave}><VscCheck size={13} /> {isNew ? 'Create' : 'Save'}</button>
        <button className="btn-secondary" onClick={onCancel}><VscClose size={13} /> Cancel</button>
      </div>
    </div>
  );
}

function JobPackageView({ project, role, currentUser, onJobPkgRead, highlightPkg, onOpenTab, socket }) {
  const [packages,  setPackages]  = useState([]);
  const [users,     setUsers]     = useState([]);
  const [allCables, setAllCables] = useState([]);
  const [allItems,  setAllItems]  = useState([]);
  const [expanded,  setExpanded]  = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData,  setEditData]  = useState(null);
  const [creating,  setCreating]  = useState(false);
  const [newData,   setNewData]   = useState(null);
  const [error,     setError]     = useState('');
  const [copiedId,      setCopiedId]      = useState(null);
  const [statusFilter,  setStatusFilter]  = useState(null); // null = all
  const highlightHandled = useRef(false);

  const canEdit = ['admin', 'engineer', 'manager', 'foreman'].includes(role);

  const handleCopy = (pkg) => {
    // "My Job Packages" has no single project — use the package's own (populated) project
    const nbNo = project?.newbuildingNo ?? pkg.project?.newbuildingNo;
    const num  = pkg.number ? padNum(pkg.number) : pkg._id;
    const text = nbNo ? `&${nbNo}-${num}` : `&${num}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(pkg._id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {});
  };

  const toggleCablePulled = async (pkgId, cable) => {
    try {
      const res = await patchCablePulled(cable._id, !cable.pulled);
      setPackages(prev => prev.map(p => p._id === pkgId
        ? { ...p, cables: p.cables.map(c => (c._id || c) === cable._id ? res.data : c) }
        : p
      ));
    } catch {}
  };

  // Photos changed in the gallery → update that cable/item in every package that holds it
  const setPhotos = (kind, id) => (photos) =>
    setPackages(prev => prev.map(p => ({
      ...p, [kind]: p[kind].map(r => (r._id || r) === id ? { ...r, photos } : r),
    })));

  const load = useCallback(() => {
    getJobPackages(project?._id || null).then(r => setPackages(r.data)).catch(() => {});
  }, [project?._id]);

  useEffect(() => {
    load();
    if (project?._id) {
      getUsers().then(r => setUsers(r.data)).catch(() => {});
      getCables(project._id).then(r => setAllCables(r.data)).catch(() => {});
      getItems(project._id).then(r => setAllItems(r.data)).catch(() => {});
    }
    highlightHandled.current = false;
  }, [project?._id, load]);

  // Auto-expand when arriving via chat link
  useEffect(() => {
    if (!highlightPkg || !packages.length || highlightHandled.current) return;
    const num = parseInt(highlightPkg, 10);
    const pkg = packages.find(p => p.number === num);
    if (!pkg) return;
    highlightHandled.current = true;
    setExpanded(pkg._id);
    const isUnread = pkg.unreadFor?.some(u => (u._id || u) === currentUser?.id);
    if (isUnread) {
      markJobPkgRead(pkg._id).catch(() => {});
      setPackages(prev => prev.map(p => p._id === pkg._id
        ? { ...p, unreadFor: p.unreadFor.filter(u => (u._id || u) !== currentUser?.id) }
        : p
      ));
      onJobPkgRead?.();
    }
  }, [packages, highlightPkg]);

  // Reload when assigned to a new package while the view is open
  useEffect(() => {
    if (!socket) return;
    const onAssigned = ({ projectId }) => {
      // Filter by project when in project view; reload all in global view
      if (project?._id && projectId && String(projectId) !== String(project._id)) return;
      load();
    };
    socket.on('jobpackage:assigned', onAssigned);
    return () => socket.off('jobpackage:assigned', onAssigned);
  }, [socket, project?._id, load]);

  const handleExpand = async (pkg) => {
    const id = pkg._id;
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    // Mark as read if unread for current user
    const isUnread = pkg.unreadFor?.some(u => (u._id || u) === currentUser?.id);
    if (isUnread) {
      await markJobPkgRead(id).catch(() => {});
      setPackages(prev => prev.map(p => p._id === id
        ? { ...p, unreadFor: p.unreadFor.filter(u => (u._id || u) !== currentUser?.id) }
        : p
      ));
      onJobPkgRead?.();
    }
  };

  const startEdit = (pkg) => {
    setEditingId(pkg._id);
    setEditData({
      project: project._id,
      name: pkg.name,
      orderNo: pkg.orderNo || '',
      description: pkg.description || '',
      status: pkg.status || 'planned',
      startDate: pkg.startDate ? isoDate(pkg.startDate) : isoDate(new Date()),
      endDate: pkg.endDate ? isoDate(pkg.endDate) : isoDate(addDays(new Date(), 14)),
      color: pkg.color || '#0e639c',
      assignees: (pkg.assignees || []).map(a => a._id || a),
      cables:    (pkg.cables    || []).map(c => c._id || c),
      items:     (pkg.items     || []).map(i => i._id || i),
      // Normalise fileId back to string (it may be a populated object)
      files: (pkg.files || []).map(f => ({
        fileId: f.fileId?._id || f.fileId,
        name:   f.name || f.fileId?.filename || '',
      })),
    });
    setError('');
  };

  const openFile = (f) => {
    const fid      = f.fileId?._id || f.fileId;
    const filename = f.name || f.fileId?.filename || 'Document';
    const ext      = filename.toLowerCase().split('.').pop();
    const fileObj  = { _id: fid, filename };
    const token    = localStorage.getItem('kspro_token');
    if (ext === 'pdf') {
      if (navigator.maxTouchPoints > 0) {
        window.open(`/api/project-files/${fid}/download?token=${encodeURIComponent(token)}`, '_blank');
      } else {
        onOpenTab?.(`pdf-${fid}`, filename, { type: 'pdf-viewer', file: fileObj });
      }
    } else if (ext === 'txt') {
      onOpenTab?.(`txt-${fid}`, filename, { type: 'txt-viewer', file: fileObj });
    } else {
      const a = document.createElement('a');
      a.href = `/api/project-files/${fid}/download?token=${encodeURIComponent(token)}`;
      a.download = filename;
      a.click();
    }
  };

  const saveEdit = async () => {
    if (!editData.name.trim()) return setError('Name is required.');
    try {
      const res = await updateJobPackage(editingId, editData);
      setPackages(prev => prev.map(p => p._id === editingId ? res.data : p));
      setEditingId(null); setEditData(null); setError('');
    } catch (e) {
      setError(e.response?.data?.msg || 'Save failed.');
    }
  };

  const handleCreate = async () => {
    if (!newData.name.trim()) return setError('Name is required.');
    try {
      const res = await createJobPackage(newData);
      setPackages(prev => [...prev, res.data]);
      setCreating(false); setNewData(null); setError('');
    } catch (e) {
      setError(e.response?.data?.msg || 'Create failed.');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this job package?')) return;
    try {
      await deleteJobPackage(id);
      setPackages(prev => prev.filter(p => p._id !== id));
      if (expanded === id) setExpanded(null);
    } catch {}
  };

  const handleStatusAdvance = async (e, pkg) => {
    e.stopPropagation();
    const next = STATUS_CYCLE[pkg.status] || 'in-progress';
    try {
      const res = await patchJobPkgStatus(pkg._id, next);
      setPackages(prev => prev.map(p => p._id === pkg._id ? res.data : p));
    } catch {}
  };

  const visiblePackages = statusFilter
    ? packages.filter(p => p.status === statusFilter)
    : packages;

  return (
    <div className="jp-wrap module-wrap">
      <div className="module-toolbar">
        <span className="module-toolbar-title">
          {project ? `Job Packages — ${project.name}` : 'My Job Packages'}
        </span>
        <div className="jp-status-filter">
          {[null, 'planned', 'in-progress', 'completed'].map(s => (
            <button
              key={s ?? 'all'}
              className={`jp-filter-btn${statusFilter === s ? ' active' : ''}`}
              style={statusFilter === s && s ? { borderColor: STATUS_COLORS[s], color: STATUS_COLORS[s] } : {}}
              onClick={() => setStatusFilter(s)}
            >
              {s ? STATUS_LABELS[s] : 'All'}
            </button>
          ))}
        </div>
        {canEdit && project && (
          <button className="btn-primary" onClick={() => {
            setCreating(true);
            setNewData(emptyForm(project._id));
            setError('');
          }}>
            <VscAdd size={13} style={{ marginRight: 4 }} /> New Package
          </button>
        )}
      </div>

      {error && <div className="row-error">{error}</div>}

      <div className="jp-body">
        {/* Read-only Gantt overview */}
        <GanttOverview packages={packages} />

        {/* Create form */}
        {creating && newData && (
          <div className="jp-card jp-card-new">
            <div className="jp-card-title">New Job Package</div>
            <PackageForm
              data={newData} setData={setNewData}
              users={users} allCables={allCables} allItems={allItems}
              onSave={handleCreate} onCancel={() => { setCreating(false); setNewData(null); setError(''); }}
              isNew
            />
          </div>
        )}

        {visiblePackages.length === 0 && !creating && (
          <div className="empty-state">
            {statusFilter
              ? `No ${STATUS_LABELS[statusFilter]?.toLowerCase()} packages.`
              : !project
                ? 'No job packages assigned to you.'
                : canEdit ? 'No job packages yet. Click "New Package" to create one.' : 'No job packages for this project.'}
          </div>
        )}

      {visiblePackages.map(pkg => {
        const isExpanded = expanded === pkg._id;
        const isEditing  = editingId === pkg._id;
        const isUnread   = pkg.unreadFor?.some(u => (u._id || u) === currentUser?.id);
        return (
          <div key={pkg._id} className={`jp-card${isUnread ? ' jp-unread' : ''}`}>
            <div className="jp-card-header" onClick={() => handleExpand(pkg)}>
              <span className="jp-card-chevron">
                {isExpanded ? <VscChevronDown size={14} /> : <VscChevronRight size={14} />}
              </span>
              {canEdit ? (
                <button
                  className="jp-status-dot jp-status-dot-btn"
                  style={{ background: STATUS_COLORS[pkg.status] || '#888' }}
                  title={`Mark as ${STATUS_LABELS[STATUS_CYCLE[pkg.status]] || 'planned'}`}
                  onClick={e => handleStatusAdvance(e, pkg)}
                />
              ) : (
                <span className="jp-status-dot" style={{ background: STATUS_COLORS[pkg.status] || '#888' }} />
              )}
              {!project && pkg.project && (
                <span className="jp-pkg-project">
                  {pkg.project.newbuildingNo ? `#${String(pkg.project.newbuildingNo).padStart(3,'0')}` : pkg.project.name}
                </span>
              )}
              {pkg.number && <span className="jp-pkg-number">{padNum(pkg.number)}</span>}
              {pkg.orderNo && <span className="jp-pkg-orderno">{pkg.orderNo}</span>}
              <span className="jp-card-name">{pkg.name}</span>
              {isUnread && <span className="jp-unread-dot" title="New assignment" />}
              <span className="jp-card-dates">{fmtDate(pkg.startDate)} – {fmtDate(pkg.endDate)}</span>
              <div className="jp-card-assignees">
                {(pkg.assignees || []).slice(0, 4).map(a => (
                  <Avatar key={a._id || a} user={a} size={20} />
                ))}
                {(pkg.assignees || []).length > 4 && <span className="jp-more-assignees">+{pkg.assignees.length - 4}</span>}
              </div>
              <span className="jp-card-status-label" style={{ color: STATUS_COLORS[pkg.status] }}>
                {STATUS_LABELS[pkg.status] || pkg.status}
              </span>
              <div className="jp-card-actions" onClick={e => e.stopPropagation()}>
                <button className="btn-icon" title="Copy chat link" onClick={() => handleCopy(pkg)}>
                  {copiedId === pkg._id ? <VscCheck size={13} /> : <VscCopy size={13} />}
                </button>
                {canEdit && (
                  <span className="jp-edit-del">
                    <button className="btn-icon" title="Edit" onClick={() => { startEdit(pkg); setExpanded(pkg._id); }}>
                      <VscEdit size={13} />
                    </button>
                    <button className="btn-icon" title="Delete" onClick={() => handleDelete(pkg._id)}>
                      <VscTrash size={13} />
                    </button>
                  </span>
                )}
              </div>
            </div>

            {isExpanded && !isEditing && (
              <div className="jp-card-body">
                {pkg.description && <p className="jp-description">{pkg.description}</p>}
                <div className="jp-detail-grid">
                  {pkg.cables?.length > 0 && (
                    <div className="jp-detail-section jp-detail-full">
                      <div className="jp-detail-title">Cables ({pkg.cables.length})</div>
                      <table className="jp-cable-table">
                        <thead>
                          <tr>
                            <th>Tag</th><th></th><th>From</th><th></th><th>To</th>
                            <th>Type</th><th>Length</th><th title="Pulled">Pull.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pkg.cables.map(c => (
                            <tr key={c._id || c}>
                              <td><span className="jp-tag">{c.tag}</span></td>
                              <td className="jp-icons-td">
                                <span className="jp-icons">
                                  <PhotoButton kind="cables" record={c} title={c.tag || 'Cable'}
                                    role={role} onPhotosChange={setPhotos('cables', c._id)} />
                                  {c.notices?.length > 0 && <RowNotices notices={c.notices} />}
                                </span>
                              </td>
                              <td className="jp-muted">{cableEndpoint(c, 'from')}</td>
                              <td className="jp-arrow">→</td>
                              <td className="jp-muted">{cableEndpoint(c, 'to')}</td>
                              <td className="jp-muted">{c.cableType?.name || '—'}</td>
                              <td className="jp-muted">{c.length_m != null ? `${c.length_m} m` : '—'}</td>
                              <td>
                                <input type="checkbox" checked={!!c.pulled}
                                  onChange={() => toggleCablePulled(pkg._id, c)}
                                  onClick={e => e.stopPropagation()} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {pkg.items?.length > 0 && (
                    <div className="jp-detail-section">
                      <div className="jp-detail-title">Items ({pkg.items.length})</div>
                      {pkg.items.map(i => (
                        <div key={i._id || i} className="jp-detail-item">
                          <span className="jp-tag">{i.tag}</span>
                          <span className="jp-icons">
                            <PhotoButton kind="items" record={i} title={i.tag || i.description || 'Item'}
                              role={role} onPhotosChange={setPhotos('items', i._id)} />
                            {i.notices?.length > 0 && <RowNotices notices={i.notices} />}
                          </span>
                          <span className="jp-muted">{i.description}</span>
                          {i.room && <span className="jp-muted">· {String(i.room.number ?? '').padStart(3,'0')} {i.room.text}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {pkg.files?.length > 0 && (
                    <div className="jp-detail-section">
                      <div className="jp-detail-title">Documents ({pkg.files.length})</div>
                      {pkg.files.map((f, i) => {
                        const fid      = f.fileId?._id || f.fileId;
                        const filename = f.name || f.fileId?.filename || 'Document';
                        return (
                          <div key={i} className="jp-detail-item">
                            <VscFile size={12} />
                            {fid
                              ? <button className="jp-file-link" onClick={() => openFile(f)}>{filename}</button>
                              : <span>{filename}</span>
                            }
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {pkg.assignees?.length > 0 && (
                    <div className="jp-detail-section">
                      <div className="jp-detail-title">Assignees</div>
                      {pkg.assignees.map(a => (
                        <div key={a._id || a} className="jp-detail-item">
                          <Avatar user={a} size={18} />
                          <span>{a.displayName || a.username || a}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {isEditing && editData && (
              <div className="jp-card-body">
                <PackageForm
                  data={editData} setData={setEditData}
                  users={users} allCables={allCables} allItems={allItems}
                  onSave={saveEdit} onCancel={() => { setEditingId(null); setEditData(null); setError(''); }}
                  isNew={false}
                />
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}

export default JobPackageView;
